import { useState } from "react";
import { AppLayout } from "@project/components/AppLayout";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Switch } from "@project/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { Textarea } from "@project/components/ui/textarea";
import { User, Building, Bell, Shield, Key, LogOut, Plus, Trash2, MapPin, Mail, Phone, Briefcase, GraduationCap, Target } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { cn } from "@project/lib/utils";
import { toast } from "@project/hooks/use-toast";

type SettingsTab = "profile" | "company" | "notifications" | "security";

const settingsTabs = [
  { key: "profile" as SettingsTab, label: "Profile", icon: User },
  // { key: "company" as SettingsTab, label: "Company", icon: Building },
  // { key: "notifications" as SettingsTab, label: "Alerts", icon: Bell },
  // { key: "security" as SettingsTab, label: "Security", icon: Shield },
];

const auditLog = [
  {
    action: "Job Created",
    user: "Admirra John",
    date: "14 Mar 2025",
    detail: "Sr. UX Designer",
  },
  {
    action: "Campaign Updated",
    user: "Manoj Shah",
    date: "13 Mar 2025",
    detail: "Q1 Engineering Drive",
  },
  {
    action: "User Added",
    user: "Admirra John",
    date: "12 Mar 2025",
    detail: "sarah.lee@company.com",
  },
  {
    action: "Settings Changed",
    user: "Admirra John",
    date: "11 Mar 2025",
    detail: "Notification preferences",
  },
  {
    action: "Job Closed",
    user: "Manoj Shah",
    date: "10 Mar 2025",
    detail: "Design Lead",
  },
  {
    action: "Password Changed",
    user: "Admirra John",
    date: "8 Mar 2025",
    detail: "Account security",
  },
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");
  const [emailNotif, setEmailNotif] = useState(true);
  const [pushNotif, setPushNotif] = useState(false);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [appUpdates, setAppUpdates] = useState(true);
  const [slackNotif, setSlackNotif] = useState(false);
  const [candidateUpdates, setCandidateUpdates] = useState(true);
  const [interviewReminder, setInterviewReminder] = useState(true);
  const [twoFA, setTwoFA] = useState(false);

  // Profile fields
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [summary, setSummary] = useState("");
  const [skillInput, setSkillInput] = useState("");
  const [skills, setSkills] = useState<string[]>([]);
  const [roleInput, setRoleInput] = useState("");
  const [eligibleRoles, setEligibleRoles] = useState<string[]>([]);
  const [experience, setExperience] = useState([
    { job_title: "", company: "", duration: "", responsibilities: "" },
  ]);
  const [education, setEducation] = useState([
    { degree: "", institution: "", year: "" },
  ]);

  return (
    <AppLayout>
      <motion.div
        className="space-y-4"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={item}>
          <h1 className="font-display text-lg font-bold tracking-tight text-foreground">
            Settings
          </h1>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            Manage your account and preferences
          </p>
        </motion.div>

        <motion.div variants={item} className="flex flex-col gap-4 lg:flex-row">
          <div className="shrink-0 lg:w-48">
            <div className="no-scrollbar flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
              {settingsTabs.map(tab => {
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                      "flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2.5 text-left transition-all lg:w-full",
                      active
                        ? "border border-border/60 bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-card/50 hover:text-foreground"
                    )}
                  >
                    <tab.icon
                      className={cn(
                        "h-4 w-4 shrink-0",
                        active ? "text-primary" : "text-muted-foreground"
                      )}
                    />
                    <span
                      className={cn(
                        "text-[13px] font-medium",
                        active && "font-semibold"
                      )}
                    >
                      {tab.label}
                    </span>
                  </button>
                );
              })}
              <button
                onClick={() => {
                  toast({
                    title: "Logged out",
                    description: "You have been logged out successfully.",
                  });
                  navigate("/");
                }}
                className="mt-2 flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2.5 text-left text-destructive transition-all hover:bg-destructive/10 sm:hidden lg:w-full"
              >
                <LogOut className="h-4 w-4 shrink-0" />
                <span className="text-[13px] font-medium">Logout</span>
              </button>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            {/* ── Profile Tab ──────────────────── */}
            {activeTab === "profile" && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                {/* Contact Information */}
                <div className="section-card overflow-hidden">
                  <div className="flex items-center gap-2 border-b border-border/50 px-4 pb-3 pt-4">
                    <User className="h-4 w-4 text-primary" />
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Contact Information
                    </h2>
                  </div>
                  <div className="space-y-3 p-4">
                    <div className="space-y-1">
                      <Label className="text-[12px] font-medium">
                        Full Name <span className="text-destructive">*</span>
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder="Your full name"
                          value={fullName}
                          onChange={e => setFullName(e.target.value)}
                          className="h-9 pl-9 text-[13px]"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-[12px] font-medium">
                          Email <span className="text-destructive">*</span>
                        </Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            type="email"
                            placeholder="your@email.com"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="h-9 pl-9 text-[13px]"
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[12px] font-medium">
                          Phone Number <span className="text-destructive">*</span>
                        </Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            placeholder="+91 9876543210"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            className="h-9 pl-9 text-[13px]"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[12px] font-medium">Location</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder="City, State, Country"
                          value={location}
                          onChange={e => setLocation(e.target.value)}
                          className="h-9 pl-9 text-[13px]"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Professional Summary */}
                <div className="section-card overflow-hidden">
                  <div className="flex items-center gap-2 border-b border-border/50 px-4 pb-3 pt-4">
                    <Briefcase className="h-4 w-4 text-primary" />
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Professional Summary
                    </h2>
                  </div>
                  <div className="p-4">
                    <Textarea
                      placeholder="Brief summary about your professional background, expertise, and career goals..."
                      value={summary}
                      onChange={e => setSummary(e.target.value)}
                      className="min-h-[80px] text-[13px]"
                    />
                  </div>
                </div>

                {/* Skills */}
                <div className="section-card overflow-hidden">
                  <div className="flex items-center gap-2 border-b border-border/50 px-4 pb-3 pt-4">
                    <Target className="h-4 w-4 text-primary" />
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Skills
                    </h2>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a skill (e.g., Python, React, SQL)"
                        value={skillInput}
                        onChange={e => setSkillInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter" && skillInput.trim()) {
                            setSkills(prev => [...prev, skillInput.trim()]);
                            setSkillInput("");
                          }
                        }}
                        className="h-9 text-[13px]"
                      />
                      <Button
                        type="button"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        onClick={() => {
                          if (skillInput.trim()) {
                            setSkills(prev => [...prev, skillInput.trim()]);
                            setSkillInput("");
                          }
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {skills.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {skills.map((skill, i) => (
                          <span
                            key={i}
                            className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-[12px] font-medium text-primary"
                          >
                            {skill}
                            <button onClick={() => setSkills(prev => prev.filter((_, idx) => idx !== i))}>
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Eligible / Preferred Roles */}
                <div className="section-card overflow-hidden">
                  <div className="flex items-center gap-2 border-b border-border/50 px-4 pb-3 pt-4">
                    <Target className="h-4 w-4 text-primary" />
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Eligible/Preferred Roles
                    </h2>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a role (e.g., AI ML Engineer, Data Scientist)"
                        value={roleInput}
                        onChange={e => setRoleInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter" && roleInput.trim()) {
                            setEligibleRoles(prev => [...prev, roleInput.trim()]);
                            setRoleInput("");
                          }
                        }}
                        className="h-9 text-[13px]"
                      />
                      <Button
                        type="button"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        onClick={() => {
                          if (roleInput.trim()) {
                            setEligibleRoles(prev => [...prev, roleInput.trim()]);
                            setRoleInput("");
                          }
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {eligibleRoles.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {eligibleRoles.map((role, i) => (
                          <span
                            key={i}
                            className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-[12px] font-medium text-primary"
                          >
                            {role}
                            <button onClick={() => setEligibleRoles(prev => prev.filter((_, idx) => idx !== i))}>
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Work Experience */}
                <div className="section-card overflow-hidden">
                  <div className="flex items-center justify-between border-b border-border/50 px-4 pb-3 pt-4">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-primary" />
                      <h2 className="font-display text-[14px] font-semibold text-foreground">
                        Work Experience
                      </h2>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-[12px]"
                      onClick={() =>
                        setExperience(prev => [
                          ...prev,
                          { job_title: "", company: "", duration: "", responsibilities: "" },
                        ])
                      }
                    >
                      <Plus className="h-3 w-3" /> Add More
                    </Button>
                  </div>
                  <div className="space-y-4 p-4">
                    {experience.map((exp, i) => (
                      <div key={i} className="space-y-3 rounded-xl border border-border/40 p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-[12px] font-semibold text-muted-foreground">
                            Experience {i + 1}
                          </p>
                          {experience.length > 1 && (
                            <button
                              onClick={() => setExperience(prev => prev.filter((_, idx) => idx !== i))}
                              className="text-destructive hover:opacity-70"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div className="space-y-1">
                            <Label className="text-[12px] font-medium">Job Title</Label>
                            <Input
                              placeholder="e.g., AI/ML Engineer"
                              value={exp.job_title}
                              onChange={e => {
                                const updated = [...experience];
                                updated[i].job_title = e.target.value;
                                setExperience(updated);
                              }}
                              className="h-9 text-[13px]"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[12px] font-medium">Company</Label>
                            <Input
                              placeholder="e.g., Universal Software"
                              value={exp.company}
                              onChange={e => {
                                const updated = [...experience];
                                updated[i].company = e.target.value;
                                setExperience(updated);
                              }}
                              className="h-9 text-[13px]"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[12px] font-medium">Duration</Label>
                          <Input
                            placeholder="e.g., Feb 2024 - Present"
                            value={exp.duration}
                            onChange={e => {
                              const updated = [...experience];
                              updated[i].duration = e.target.value;
                              setExperience(updated);
                            }}
                            className="h-9 text-[13px]"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[12px] font-medium">Key Responsibilities</Label>
                          <Textarea
                            placeholder="Describe your key responsibilities and achievements..."
                            value={exp.responsibilities}
                            onChange={e => {
                              const updated = [...experience];
                              updated[i].responsibilities = e.target.value;
                              setExperience(updated);
                            }}
                            className="min-h-[70px] text-[13px]"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Education */}
                <div className="section-card overflow-hidden">
                  <div className="flex items-center justify-between border-b border-border/50 px-4 pb-3 pt-4">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4 text-primary" />
                      <h2 className="font-display text-[14px] font-semibold text-foreground">
                        Education
                      </h2>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-[12px]"
                      onClick={() =>
                        setEducation(prev => [
                          ...prev,
                          { degree: "", institution: "", year: "" },
                        ])
                      }
                    >
                      <Plus className="h-3 w-3" /> Add More
                    </Button>
                  </div>
                  <div className="space-y-4 p-4">
                    {education.map((edu, i) => (
                      <div key={i} className="space-y-3 rounded-xl border border-border/40 p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-[12px] font-semibold text-muted-foreground">
                            Education {i + 1}
                          </p>
                          {education.length > 1 && (
                            <button
                              onClick={() => setEducation(prev => prev.filter((_, idx) => idx !== i))}
                              className="text-destructive hover:opacity-70"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[12px] font-medium">Degree / Qualification</Label>
                          <Input
                            placeholder="e.g., B.Tech in Computer Science Engineering"
                            value={edu.degree}
                            onChange={e => {
                              const updated = [...education];
                              updated[i].degree = e.target.value;
                              setEducation(updated);
                            }}
                            className="h-9 text-[13px]"
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div className="space-y-1">
                            <Label className="text-[12px] font-medium">Institution / University</Label>
                            <Input
                              placeholder="e.g., Institute of Advanced Research"
                              value={edu.institution}
                              onChange={e => {
                                const updated = [...education];
                                updated[i].institution = e.target.value;
                                setEducation(updated);
                              }}
                              className="h-9 text-[13px]"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[12px] font-medium">Graduation Year</Label>
                            <Input
                              placeholder="e.g., 2025"
                              value={edu.year}
                              onChange={e => {
                                const updated = [...education];
                                updated[i].year = e.target.value;
                                setEducation(updated);
                              }}
                              className="h-9 text-[13px]"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    className="btn-glow h-8 rounded-lg px-4 text-[12px] font-semibold"
                    onClick={() =>
                      toast({
                        title: "Saved",
                        description: "Profile updated successfully.",
                      })
                    }
                  >
                    Save Profile
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── Company Tab ──────────────────── */}
            {activeTab === "company" && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Company Details
                    </h2>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {[
                        { label: "Company Name", value: "Universal Software" },
                        {
                          label: "Legal Entity Name",
                          value: "Universal Software Pvt. Ltd.",
                        },
                        { label: "Industry", value: "Technology" },
                        {
                          label: "Website",
                          value: "https://universalsoftware.com",
                        },
                        { label: "HQ Location", value: "Bengaluru, India" },
                        { label: "Founded Year", value: "2015" },
                      ].map(f => (
                        <div key={f.label} className="space-y-1">
                          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {f.label}
                          </Label>
                          <Input
                            defaultValue={f.value}
                            className="h-9 rounded-lg bg-background text-[13px]"
                          />
                        </div>
                      ))}
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Company Size
                        </Label>
                        <Select defaultValue="201-500">
                          <SelectTrigger className="h-9 rounded-lg bg-background text-[13px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-50">1-50</SelectItem>
                            <SelectItem value="51-200">51-200</SelectItem>
                            <SelectItem value="201-500">201-500</SelectItem>
                            <SelectItem value="501-1000">501-1000</SelectItem>
                            <SelectItem value="1000+">1000+</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Company Type
                        </Label>
                        <Select defaultValue="private">
                          <SelectTrigger className="h-9 rounded-lg bg-background text-[13px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="private">Private</SelectItem>
                            <SelectItem value="public">Public</SelectItem>
                            <SelectItem value="startup">Startup</SelectItem>
                            <SelectItem value="nonprofit">
                              Non-Profit
                            </SelectItem>
                            <SelectItem value="government">
                              Government
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Branding & Social
                    </h2>
                  </div>
                  <div className="space-y-3 p-4">
                    <div className="space-y-1">
                      <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Company Description
                      </Label>
                      <Textarea
                        defaultValue="Universal Software is a technology company building innovative HR and recruitment solutions."
                        className="min-h-[60px] rounded-lg bg-background text-[13px]"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Logo URL
                        </Label>
                        <Input
                          defaultValue="https://universalsoftware.com/logo.png"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Careers Page URL
                        </Label>
                        <Input
                          defaultValue="https://universalsoftware.com/careers"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          LinkedIn
                        </Label>
                        <Input
                          defaultValue="https://linkedin.com/company/universalsoftware"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Glassdoor
                        </Label>
                        <Input
                          placeholder="https://glassdoor.com/..."
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                    </div>
                    <Button
                      className="mt-2 h-8 rounded-lg text-[12px] font-semibold"
                      onClick={() =>
                        toast({
                          title: "Saved",
                          description: "Company details updated.",
                        })
                      }
                    >
                      Update Company
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Notifications Tab ──────────────────── */}
            {activeTab === "notifications" && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      General Notifications
                    </h2>
                  </div>
                  <div className="space-y-0.5 p-4">
                    {[
                      {
                        label: "Email Notifications",
                        desc: "Receive updates via email",
                        checked: emailNotif,
                        onChange: setEmailNotif,
                      },
                      {
                        label: "Push Notifications",
                        desc: "Browser push notifications",
                        checked: pushNotif,
                        onChange: setPushNotif,
                      },
                      {
                        label: "Slack Notifications",
                        desc: "Get alerts in Slack channel",
                        checked: slackNotif,
                        onChange: setSlackNotif,
                      },
                      {
                        label: "Weekly Digest",
                        desc: "Summary of weekly activity",
                        checked: weeklyDigest,
                        onChange: setWeeklyDigest,
                      },
                      {
                        label: "App Updates",
                        desc: "New features & release notes",
                        checked: appUpdates,
                        onChange: setAppUpdates,
                      },
                    ].map(n => (
                      <div
                        key={n.label}
                        className="flex items-center justify-between rounded-xl p-3 transition-colors hover:bg-accent/20"
                      >
                        <div>
                          <p className="text-[13px] font-semibold text-foreground">
                            {n.label}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {n.desc}
                          </p>
                        </div>
                        <Switch
                          checked={n.checked}
                          onCheckedChange={n.onChange}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Recruitment Alerts
                    </h2>
                  </div>
                  <div className="space-y-0.5 p-4">
                    {[
                      {
                        label: "New Application Received",
                        desc: "When a candidate applies to your job",
                        checked: candidateUpdates,
                        onChange: setCandidateUpdates,
                      },
                      {
                        label: "Interview Reminders",
                        desc: "30 min before scheduled interviews",
                        checked: interviewReminder,
                        onChange: setInterviewReminder,
                      },
                      {
                        label: "Offer Accepted/Rejected",
                        desc: "When a candidate responds to an offer",
                        checked: true,
                        onChange: () => {},
                      },
                      {
                        label: "Job Posting Expiry",
                        desc: "3 days before a posting expires",
                        checked: true,
                        onChange: () => {},
                      },
                      {
                        label: "Referral Status Updates",
                        desc: "When referred candidates move stages",
                        checked: false,
                        onChange: () => {},
                      },
                    ].map(n => (
                      <div
                        key={n.label}
                        className="flex items-center justify-between rounded-xl p-3 transition-colors hover:bg-accent/20"
                      >
                        <div>
                          <p className="text-[13px] font-semibold text-foreground">
                            {n.label}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {n.desc}
                          </p>
                        </div>
                        <Switch
                          checked={n.checked}
                          onCheckedChange={n.onChange}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Security Tab ──────────────────── */}
            {activeTab === "security" && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Password & Authentication
                    </h2>
                  </div>
                  <div className="space-y-3 p-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Current Password
                        </Label>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                      <div />
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          New Password
                        </Label>
                        <Input
                          type="password"
                          placeholder="Min 8 characters"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Confirm New Password
                        </Label>
                        <Input
                          type="password"
                          placeholder="Re-enter password"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="h-8 rounded-lg text-[12px] font-semibold"
                      onClick={() =>
                        toast({
                          title: "Updated",
                          description: "Password changed successfully.",
                        })
                      }
                    >
                      Update Password
                    </Button>
                  </div>
                </div>

                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Two-Factor Authentication
                    </h2>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between rounded-xl border border-border/30 bg-muted/30 p-3">
                      <div>
                        <p className="text-[13px] font-semibold text-foreground">
                          Enable 2FA
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Add an extra layer of security with authenticator app
                        </p>
                      </div>
                      <Switch checked={twoFA} onCheckedChange={setTwoFA} />
                    </div>
                    <div className="mt-3 space-y-1">
                      <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Session Timeout
                      </Label>
                      <Select defaultValue="30">
                        <SelectTrigger className="h-9 max-w-xs rounded-lg bg-background text-[13px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 minutes</SelectItem>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">1 hour</SelectItem>
                          <SelectItem value="480">8 hours</SelectItem>
                          <SelectItem value="never">Never</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Audit Log
                    </h2>
                  </div>
                  {/* Mobile */}
                  <div className="divide-y divide-border/30 sm:hidden">
                    {auditLog.map((log, i) => (
                      <div key={i} className="px-4 py-3">
                        <div className="flex items-center justify-between">
                          <p className="text-[12px] font-medium text-foreground">
                            {log.action}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {log.date}
                          </p>
                        </div>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {log.user} · {log.detail}
                        </p>
                      </div>
                    ))}
                  </div>
                  {/* Desktop */}
                  <div className="hidden overflow-x-auto sm:block">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-border/50 bg-muted/30 hover:bg-muted/30">
                          {["Action", "User", "Date", "Detail"].map(h => (
                            <TableHead
                              key={h}
                              className="py-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/50"
                            >
                              {h}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {auditLog.map((log, i) => (
                          <TableRow
                            key={i}
                            className="table-row-hover border-b border-border/30 last:border-0"
                          >
                            <TableCell className="py-3 text-[12px] font-medium text-foreground">
                              {log.action}
                            </TableCell>
                            <TableCell className="py-3 text-[12px] text-muted-foreground">
                              {log.user}
                            </TableCell>
                            <TableCell className="py-3 text-[12px] text-muted-foreground">
                              {log.date}
                            </TableCell>
                            <TableCell className="py-3 text-[12px] text-muted-foreground">
                              {log.detail}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AppLayout>
  );
};

export default SettingsPage;
